import {FC, Component, useMemo, ReactNode} from 'react';
import {action, computed, makeObservable} from 'mobx';
import {Observer, observer} from 'mobx-react';
import {Form, Table, Input, Icon, Button} from 'semantic-ui-react';
import {
  map, transform, filter, keys, size, union, pullAllWith, isMatch, isEmpty,
  isString, isPlainObject, uniqBy, forEach, some, includes, has,
} from 'lodash';
import {DropdownControl, Field, MapInput} from 'apstra-ui-common';

import {generateNewInputName, getMatchingStageValueSchemas, getPossibleStageValues} from '../stageUtils';
import {Probe, ProbeProcessor, ProcessorDefinition, ProcessorInput} from '../types';
import {ProbeError} from './ProbeDetails/types';

import './ProcessorInputEditor.less';

type ColumnOption = {name: string; title?: ReactNode};

type ProcessorInputEditorProps = {
  probe: Probe;
  processor: ProbeProcessor;
  processorDefinitionsByName: Record<string, ProcessorDefinition>;
  highlightStage: (stage: string, highlight: boolean) => void;
  errors: ProbeError[];
  actionInProgress?: boolean;
};

@observer
export default class ProcessorInputEditor extends Component<ProcessorInputEditorProps> {
  constructor(props) {
    super(props);
    makeObservable(this);
  }

  getAvailableInputColumnsByStage = (inputName: string): Record<string, ColumnOption[]> => {
    const {probe, processor: destinationProcessor, processorDefinitionsByName} = this.props;
    const destinationProcessorDefinition = processorDefinitionsByName[destinationProcessor.name];
    const inputDefinition = destinationProcessorDefinition.inputs[inputName];
    const inputValues = getPossibleStageValues(inputDefinition);
    return transform(probe.processors, (result, sourceProcessor) => {
      if (sourceProcessor.name === destinationProcessor.name) return [];
      const sourceProcessorDefinition = processorDefinitionsByName[sourceProcessor.name];
      forEach(keys(sourceProcessorDefinition.outputs), (outputName) => {
        const outputDefinition = sourceProcessorDefinition.outputs[outputName];
        const outputValues = getPossibleStageValues(outputDefinition);
        const columns: ColumnOption[] = map(
          uniqBy(getMatchingStageValueSchemas(inputValues, outputValues), 'name'),
          ({name, title}) => ({name, title})
        );
        if (inputDefinition.allow_column_wildcard) {
          columns.unshift({name: '*', title: <b>{'All Columns'}</b>});
        }
        if (!isEmpty(columns)) {
          result[sourceProcessor.outputs[outputName]] = columns;
        }
      });
    }, {});
  };

  @action
  onInputChange = (inputName: string, input?: ProcessorInput) => {
    const {processor, errors, highlightStage} = this.props;
    if (input) {
      processor.inputs[inputName] = input;
      if (input.stage) highlightStage(input.stage, false);
    } else {
      delete processor.inputs[inputName];
    }
    pullAllWith(errors, [
      {type: 'processors'},
      {type: 'processor', processorName: processor.name},
      {type: 'processorInput', processorName: processor.name, inputName},
      {type: 'processorInput', processorName: processor.name, inputName: null},
    ], isMatch);
  };

  @action
  removeOptionalInput = (inputName) => {
    const {processor, errors} = this.props;
    delete processor.inputs[inputName];
    pullAllWith(errors, [
      {type: 'processors'},
      {type: 'processor', processorName: processor.name},
      {type: 'processorInput', processorName: processor.name, inputName},
    ], isMatch);
  };

  @action
  addOptionalInput = () => {
    const {availableOptionalInputs, props: {processor}} = this;
    if (availableOptionalInputs.length) processor.inputs[availableOptionalInputs[0]] = {};
  };

  @action
  onInputNameChange = (oldInputName: string, newInputName: string) => {
    const {processor, errors} = this.props;
    const value = processor.inputs[oldInputName];
    delete processor.inputs[oldInputName];
    processor.inputs[newInputName] = value;
    pullAllWith(errors, [
      {type: 'processors'},
      {type: 'processor', processorName: processor.name},
      {type: 'processorInput', processorName: processor.name, inputName: oldInputName},
    ], isMatch);
  };

  @computed
  get availableOptionalInputs() {
    const {processor, processorDefinitionsByName} = this.props;
    const processorDefinition = processorDefinitionsByName[processor.name];
    const usedInputs = keys(processor.inputs);
    return transform(processorDefinition?.inputs, (result, inputDefinition, inputName) => {
      if (!includes(usedInputs, inputName) && !inputDefinition.required) result.push(inputName);
    }, [] as string[]);
  }

  render() {
    const {processor, highlightStage, actionInProgress, errors, processorDefinitionsByName} = this.props;
    const processorDefinition = processorDefinitionsByName[processor.name];
    if (!size(processorDefinition.inputs)) return null;
    const hasMultipleInputs = has(processorDefinition.inputs, '*');
    const hasOptionalInputs = some(processorDefinition.inputs, {required: false});
    const inputs = hasMultipleInputs ?
      processorDefinition.inputs : processor.inputs;
    return (
      <Table size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{'Inputs'}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.Cell>
              <Form>
                {map(inputs, (inputDefinition, inputName) => {
                  const InputEditorComponent = inputName === '*' ? MultipleInputsEditor : SingleInputEditor;
                  const required = processorDefinition.inputs[inputName]?.required;
                  return (
                    <div key={inputName} className='input-component-wrapper'>
                      <div className='input-component'>
                        <InputEditorComponent
                          inputName={inputName}
                          processor={processor}
                          processorDefinition={processorDefinition}
                          required={required}
                          disabled={actionInProgress}
                          errors={errors}
                          availableColumnsByStage={this.getAvailableInputColumnsByStage(inputName)}
                          highlightStage={highlightStage}
                          onInputChange={this.onInputChange}
                          onInputNameChange={this.onInputNameChange}
                          availableProcessorInputs={this.availableOptionalInputs}
                        />
                      </div>
                      {hasOptionalInputs && (
                        <Icon
                          link
                          name='remove'
                          color={required ? 'grey' : 'red'}
                          disabled={required}
                          onClick={() => this.removeOptionalInput(inputName)}
                          aria-label='Remove'
                          className='icon'
                        />
                      )}
                    </div>
                  );
                })}
              </Form>
              {this.availableOptionalInputs.length > 0 && (
                <Button
                  type='button'
                  color='teal'
                  size='tiny'
                  icon='add'
                  labelPosition='left'
                  content='Add Input'
                  onClick={this.addOptionalInput}
                />
              )}
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    );
  }
}

type InputEditorProps = {
  inputName: string;
  processor: ProbeProcessor;
  processorDefinition: ProcessorDefinition;
  availableProcessorInputs: string[];
  availableColumnsByStage: Record<string, ColumnOption[]>;
  required?: boolean;
  disabled?: boolean;
  errors?: ProbeError[];
  highlightStage: (stage: string, highlight: boolean) => void;
  onInputChange: (inputName: string, input?: ProcessorInput) => void;
  onInputNameChange: (oldInputName: string, newInputName: string) => void;
};

const SingleInputEditor: FC<InputEditorProps> = observer(({
  inputName, processor, processorDefinition, required, disabled, availableProcessorInputs,
  errors, availableColumnsByStage, highlightStage, onInputChange, onInputNameChange,
}) => {
  const errorMessages = map(filter(errors, {
    type: 'processorInput',
    processorName: processor.name,
    inputName
  }), 'message');
  const inputDefinition = processorDefinition.inputs[inputName];
  return (
    <>
      <InputSelectorHeader required={required} label='Input Stage' />
      <Form.Group>
        <Field disabled={disabled} width={3}>
          <DropdownControl
            placeholder='Input Name'
            value={inputName}
            options={map(availableProcessorInputs, (inputName) => ({
              key: inputName,
              value: inputName,
              text: inputName,
            }))}
            onChange={(newInputName) => onInputNameChange(inputName, newInputName)}
          />
        </Field>
        <Field
          width={13}
          disabled={disabled}
          errors={filter(errorMessages, isString)}
          description={inputDefinition?.description}
          className='multi-items-input'
        >
          <StageAndColumnEditor
            inputName={inputName}
            value={processor.inputs[inputName]}
            disabled={disabled}
            availableColumnsByStage={availableColumnsByStage}
            highlightStage={highlightStage}
            onInputChange={onInputChange}
            defaultColumnName={inputDefinition?.allow_column_wildcard ? '*' : undefined}
            errors={filter(errorMessages, isPlainObject)}
          />
        </Field>
      </Form.Group>
    </>
  );
});

const MultipleInputsEditor: FC<InputEditorProps> = observer(({
  processor, processorDefinition, required, disabled,
  errors, availableColumnsByStage, highlightStage, onInputChange
}) => {
  const value = transform(processor.inputs, (result, value, inputName) => {
    if (!processorDefinition.inputs[inputName]) result[inputName] = value;
  }, {});
  const generateNewEntry = () => [generateNewInputName(processor), {}];
  const onChange = (newValue) => {
    for (const key of union(keys(value), keys(newValue))) {
      if ((key in value) && !(key in newValue)) {
        onInputChange(key);
      } else if (!(key in value) && (key in newValue)) {
        onInputChange(key, newValue[key]);
      }
    }
  };
  const genericErrors = map(filter(errors, (error) =>
    error.type === 'processorInput' &&
    error.processorName === processor.name &&
    error.inputName === null
  ), 'message');

  return (
    <>
      <InputSelectorHeader
        isEmptyValue={isEmpty(value)}
        required={required}
        label='Input Stages'
        multiple
      />
      <MapInput
        value={value}
        disabled={disabled}
        generateNewEntry={generateNewEntry}
        onChange={onChange}
        buttonText='Add Input Stage'
        noItemsMessage='There are no input stages defined.'
        errors={genericErrors}
      >
        {({key: inputName, value, index, setEntryKey: setInputName}) =>
          <Observer>
            {() => {
              const errorMessages = map(filter(errors, {
                type: 'processorInput',
                processorName: processor.name,
                inputName
              }), 'message');
              return (
                <>
                  <Field key='name' width={3}>
                    <Input
                      placeholder='Input Name'
                      disabled={disabled}
                      value={inputName}
                      onChange={(e) => setInputName(index, e.target.value)}
                    />
                  </Field>
                  <Field
                    key='value'
                    width={13}
                    className='multi-items-input'
                    errors={filter(errorMessages, isString)}
                    disabled={disabled}
                  >
                    <StageAndColumnEditor
                      inputName={inputName}
                      value={value}
                      disabled={disabled}
                      availableColumnsByStage={availableColumnsByStage}
                      highlightStage={highlightStage}
                      onInputChange={onInputChange}
                      errors={filter(errorMessages, isPlainObject)}
                    />
                  </Field>
                </>
              );
            }}
          </Observer>
        }
      </MapInput>
    </>
  );
});

type StageAndColumnEditorProps = {
  value: ProcessorInput;
  inputName: string;
  onInputChange: (inputName: string, input?: ProcessorInput) => void;
  errors: any[];
  disabled?: boolean;
  availableColumnsByStage: Record<string, ColumnOption[]>;
  highlightStage: (stage: string, highlight: boolean) => void;
  defaultColumnName?: string;
};

const StageAndColumnEditor: FC<StageAndColumnEditorProps> = observer(({
  inputName, value, onInputChange, errors, disabled, availableColumnsByStage, highlightStage, defaultColumnName
}) => {
  const {stage: stageName, column: columnName} = value ?? {};

  const stageDropdownOptions = useMemo(
    () => {
      const availableStageNames = keys(availableColumnsByStage);
      return map(availableStageNames, (stageName) => ({
        key: stageName,
        value: stageName,
        text: stageName,
        onMouseEnter: () => highlightStage(stageName, true),
        onMouseLeave: () => highlightStage(stageName, false),
      }));
    },
    [availableColumnsByStage, highlightStage]
  );

  const columnDropdownOptions = useMemo(() => {
    const availableColumns = stageName ? availableColumnsByStage[stageName] : null;
    return map(availableColumns, ({name, title}) => ({
      key: name,
      value: name,
      text: title ?? name,
    }));
  }, [stageName, availableColumnsByStage]);

  return (
    <Form.Group>
      <Field
        width={8}
        errors={filter(map(errors, 'stage'))}
        disabled={!stageDropdownOptions.length || disabled}
      >
        <DropdownControl
          search
          clearable
          placeholder={stageDropdownOptions.length ? 'Choose input stage' : 'No available stages'}
          value={stageName ?? ''}
          options={stageDropdownOptions}
          onChange={(stageName) => onInputChange(
            inputName,
            stageName === '' ? {} : {stage: stageName, column: defaultColumnName},
          )}
        />
      </Field>
      <Field
        width={8}
        errors={filter(map(errors, 'column'))}
        disabled={!columnDropdownOptions.length || disabled}
      >
        <DropdownControl
          clearable
          placeholder={columnDropdownOptions.length ? 'Choose value column name' : 'No available columns'}
          value={columnName ?? ''}
          options={columnDropdownOptions}
          onChange={
            (value) => onInputChange(
              inputName,
              value === '' ? {stage: stageName} : {stage: stageName, column: value}
            )
          }
        />
      </Field>
    </Form.Group>
  );
});

type InputSelectorHeaderProps = {
  label: string;
  required?: boolean;
  multiple?: boolean;
  isEmptyValue?: boolean;
};

const InputSelectorHeader: FC<InputSelectorHeaderProps> = observer(({label, required, multiple, isEmptyValue}) => (
  <Field required={required} label={label} className='processor-input-header'>
    {!isEmptyValue && (
      <Form.Group className='processor-input-subheader'>
        <Form.Field key='name' width={3} label='Input Name' />
        <Form.Field key='value' width={13} className='multi-items-input'>
          <Form.Group>
            <Form.Field width={8} label='Stage Name' />
            <Form.Field width={8} label='Column Name' />
          </Form.Group>
        </Form.Field>
        {multiple && (
          <Form.Field className='no-input-field hidden-icon'>
            <Icon name='remove' />
          </Form.Field>
        )}
      </Form.Group>
    )}
  </Field>
));
